import { AfterViewInit, Component, forwardRef, Input, TemplateRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject, Observable } from 'rxjs';

import { StripeService } from './stripe.service';

@Component({
  selector: 'om-stripe-credit-card',
  templateUrl: './stripe-credit-card.component.html',
  styleUrls: ['./stripe-credit-card.component.scss'],
  providers: [
    StripeService,
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => StripeCreditCardComponent),
    },
  ],
})
export class StripeCreditCardComponent implements AfterViewInit, ControlValueAccessor {
  static readonly STYLE: any = {
    base: {
      fontSize: '16px',
      fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
      fontWeight: '300',
    },
  };

  @Input() customTemplate?: TemplateRef<any>;
  @Input() customStyle?: any;
  @Input() externalErrors = false;
  @Input() showNicknameField = false;
  @Input() cardId?: string;
  @Input() autoFocus = true;

  nickname: string;

  private cardEntryElement: stripe.elements.Element;
  private propagateChange: (change: { postalCode: string | number } | string | undefined) => void;
  private propagateError: (response: stripe.elements.ElementChangeResponse) => void;
  private propagateComplete: (response: stripe.elements.ElementChangeResponse) => void;

  constructor(private stripeService: StripeService) {}

  ngAfterViewInit() {
    this.cardEntryElement = this.stripeService.createElement('card', {
      style: this.customStyle ? this.customStyle : StripeCreditCardComponent.STYLE,
    });

    this.cardEntryElement.mount(this.cardId ? `#card-element-${this.cardId}` : '#card-element');

    this.cardEntryElement.on(
      'ready',
      (() => {
        if (this.autoFocus) {
          this.cardEntryElement.focus();
        }
      }).bind(this),
    );

    this.cardEntryElement.on(
      'change',
      ((e: stripe.elements.ElementChangeResponse) => {
        if (this.propagateChange) {
          this.propagateChange(e.value);
        }
        if (this.propagateComplete && e.complete) {
          this.propagateComplete(e);
        }
        if (this.propagateError && e.error) {
          this.propagateError(e);
        }
      }).bind(this),
    );
  }

  writeValue(value: any) {
    if (!this.cardEntryElement) {
      return;
    }
    this.cardEntryElement.update({ value: value });
  }

  registerOnChange(fn: (change: { postalCode: string | number } | string | undefined) => void) {
    this.propagateChange = fn;
  }

  registerOnError(fn: (response: stripe.elements.ElementChangeResponse) => void) {
    this.propagateError = fn;
  }

  registerOnComplete(fn: (response: stripe.elements.ElementChangeResponse) => void) {
    this.propagateComplete = fn;
  }

  registerOnTouched(_fn: any) {}

  generateToken(): Observable<stripe.Token> {
    const tokenResponse = new Subject<stripe.Token>();
    this.stripeService.createToken(this.cardEntryElement).then((response: stripe.TokenResponse) => {
      if (response.error) {
        tokenResponse.error(response.error.message);
      } else {
        tokenResponse.next(response.token);
      }
    });
    return tokenResponse.asObservable();
  }

  confirmSetupIntent(setupIntentClientSecret: string): Observable<stripe.setupIntents.SetupIntent> {
    const setupIntentResponse = new Subject<stripe.setupIntents.SetupIntent>();
    this.stripeService
      .confirmCardSetup(setupIntentClientSecret, this.cardEntryElement)
      .then((response: stripe.SetupIntentResponse) => {
        if (response.error) {
          setupIntentResponse.error(response.error.message);
        } else {
          setupIntentResponse.next(response.setupIntent);
        }
      });
    return setupIntentResponse.asObservable();
  }
}
